import React, { useState, useEffect } from "react";
import signupimg from "../components/img/signupnew.svg";
import callIcon from "../components/img/call_icon.svg";
import emailIcon from "../components/img/EmailIcon.svg";
import UserIcon from "../components/img/UserIcon.svg";
import CompanyIcon from "../components/img/CompanyIcon.svg";
import LockIcon from "../components/img/LockIcon.svg";
import { useNavigate, useLocation } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import api from "../api/axiosInstance";
import { Eye, EyeOff } from "lucide-react";

const SignUp = () => {
  const location = useLocation();
  const { type, value } = location.state || {};
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [domain, setDomain] = useState("");
  const [businessEmail, setBusinessEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isEmailDisabled, setIsEmailDisabled] = useState(false);
  const [isPhoneDisabled, setIsPhoneDisabled] = useState(false);
  const [countryCode, setCountryCode] = useState("");
  const [password, setPassword] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [navigateAfterModal, setNavigateAfterModal] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
  const [passwordStrengthMessage, setPasswordStrengthMessage] = useState("");
  const [passwordStrengthColor, setPasswordStrengthColor] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);

  const prefix = "+91";

  const handleInputFocus = (event) => {
    const input = event.target;
    if (input.selectionStart < prefix.length) {
      input.setSelectionRange(prefix.length, prefix.length);
    }
  };

  useEffect(() => {
    if (type === "email" && value) {
      setBusinessEmail(value);
      setIsEmailDisabled(true);
    } else if (type === "phone" && value) {
      let cleaned = value.replace(/\D/g, "");

      if (cleaned.length === 10) {
        setPhoneNumber("91" + cleaned); // ✅ no +
        setCountryCode("+91");
      } else if (cleaned.startsWith("91") && cleaned.length === 12) {
        setPhoneNumber(cleaned); // ✅ already good
        setCountryCode("+91");
      } else if (cleaned.startsWith("0") && cleaned.length === 11) {
        setPhoneNumber("+91" + cleaned.slice(1));
        setCountryCode("+91");
      } else {
        setPhoneNumber("+" + cleaned);
        setCountryCode("+" + cleaned.slice(0, cleaned.length - 10)); // fallback
      }

      setIsPhoneDisabled(true);
    }
  }, [type, value]);

  const handleKeyDown = (event) => {
    const input = event.target;
    if (
      (event.key === "Backspace" || event.key === "Delete") &&
      input.selectionStart <= prefix.length
    ) {
      event.preventDefault();
    }
  };

  const handlePhoneInputKeyDown = (e) => {
    const inputValue = e.target.value;
    const countryDialCode = countryCode || "+91"; // fallback to +91
    const dialCodeLength = countryDialCode.length;
    const cursorPosition = e.target.selectionStart;

    if (
      (e.key === "Backspace" || e.key === "Delete") &&
      cursorPosition <= dialCodeLength
    ) {
      e.preventDefault(); // prevent deleting the country code
    }
  };

  const handleRegister = async (e = null, skipEmailValidation = false) => {
    if (e) e.preventDefault();

    const validationErrors = {};

    if (!confirmPassword.trim()) {
      validationErrors.confirmPassword = "Enter your confirm password.";
    } else if (confirmPassword !== password) {
      validationErrors.confirmPassword = "Passwords do not match.";
    }

    if (!acceptTerms) {
      validationErrors.terms = "Please tick this box to proceed.";
    }

    if (!name.trim()) validationErrors.name = "Enter your name.";
    if (!companyName.trim())
      validationErrors.company_name = "Enter your company name.";
    if (!domain.trim()) validationErrors.domain = "Enter your subdomain.";

    // if (!phoneNumber.trim())
    //   validationErrors.phone_number = "Enter your valid mobile number.";
    if (!password.trim()) {
      validationErrors.password = "Enter your password.";
    }

    const cleanedPhone = phoneNumber.replace(/\D/g, ""); // remove non-numeric

    let extractedPhoneNumber = "";
    let extractedCountryCode = "";

    if (cleanedPhone.length < 11) {
      validationErrors.phone_number = "Enter your valid mobile number.";
    } else {
      extractedPhoneNumber = cleanedPhone.slice(-10);
      extractedCountryCode =
        "+" + cleanedPhone.slice(0, cleanedPhone.length - 10);

      // Optional: Validate India numbers specifically
      if (
        extractedCountryCode === "+91" &&
        !/^[6-9]\d{9}$/.test(extractedPhoneNumber)
      ) {
        validationErrors.phone_number = "Enter a valid 10-digit Indian number.";
      }
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (!skipEmailValidation && !businessEmail.trim()) {
      setShowEmailConfirmation(true); // Always show modal
      return;
    }

    // const fullDomain = `${domain}`;
    setLoading(true);

    try {
      const response = await api.post("/display/api/v1/signup-user", {
        first_name: name,
        company_name: companyName,
        domain: domain,
        email: businessEmail,
        phone_number: extractedPhoneNumber,
        country_code: extractedCountryCode,
        password: password,
      });

      setLoading(false);

      if (response.status === 201 || response.data.status === "success") {
        const { refresh, access, user_id } = response.data.data;
        localStorage.setItem("accessToken", access);
        localStorage.setItem("refreshToken", refresh);
        localStorage.setItem("user_id", user_id);
        showModal("Account successfully created.");
        setNavigateAfterModal(true); // set flag to navigate after modal closes
      } else {
        showModal(response.data.message || "Registration failed.");
      }
    } catch (error) {
      console.error("Registration error:", error);
      setLoading(false);
      if (error.response && error.response.data) {
        const serverErrors = error.response.data.errors || {};
        setErrors(serverErrors);

        if (error.response.data.message) {
          showModal(error.response.data.message);
        }
      } else {
        showModal("Something went wrong. Please try again.");
      }
    }
  };

  const handleProceedWithoutEmail = async () => {
    const currentCountryCode = countryCode || "+91";

    if (currentCountryCode !== "+91") {
      setShowEmailConfirmation(false);
      setErrors((prev) => ({
        ...prev,
        email: "Email ID is required for international users.",
      }));
      showModal("Email ID is mandatory for users outside India.");
      return;
    }

    setShowEmailConfirmation(false);
    setTimeout(() => {
      handleRegister(null, true);
    }, 100);
  };

  const handleCancelWithoutEmail = () => {
    setShowEmailConfirmation(false);
    setErrors((prev) => ({
      ...prev,
      email: "",
    }));
  };

  const showModal = (message) => {
    setModalMessage(message);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalMessage("");
    if (navigateAfterModal) {
      setNavigateAfterModal(false);
      navigate("/dashboard");
    }
  };

  const handleClick = () => {
    navigate("/mainLogin");
  };

  useEffect(() => {
    const cleanCompany = companyName.toLowerCase().replace(/[^a-z0-9-]/g, ""); // Remove all non-alphanumeric and non-hyphen characters
    setDomain(cleanCompany);
  }, [companyName]);

  const evaluatePasswordStrength = (pwd) => {
    const hasUpperCase = /[A-Z]/.test(pwd);
    const hasLowerCase = /[a-z]/.test(pwd);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(pwd);
    const hasMinLength = pwd.length >= 8;

    if (!pwd) {
      setPasswordStrengthMessage("");
      return;
    }

    if (!hasUpperCase) {
      setPasswordStrengthMessage("Include at least one uppercase letter.");
      setPasswordStrengthColor("text-red-600");
    } else if (!hasLowerCase) {
      setPasswordStrengthMessage("Include at least one lowercase letter.");
      setPasswordStrengthColor("text-red-600");
    } else if (!hasSpecialChar) {
      setPasswordStrengthMessage("Include at least one special character.");
      setPasswordStrengthColor("text-red-600");
    } else if (!hasMinLength) {
      setPasswordStrengthMessage("Password must be at least 8 characters.");
      setPasswordStrengthColor("text-red-600");
    } else {
      setPasswordStrengthMessage("");
      setPasswordStrengthColor("text-green-600");
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      <div
        className="w-full lg:w-1/2 min-h-[300px] lg:min-h-screen bg-no-repeat bg-center bg-cover"
        style={{ backgroundImage: `url(${signupimg})` }}
      ></div>

      <div className="w-full lg:w-1/2 flex overflow-y-auto  justify-center items-center px-4 lg:px-8 py-10">
        <div className="w-full max-w-md max-h-[85vh]">
          <h2 className="text-2xl font-bold text-center text-black mb-2 font-inter">
            Sign Up
          </h2>
          <p className="text-center mb-2 font-inter text-sm text-black">
            Welcome to Onzitr Marketplace!
          </p>

          <p className="text-[#E8505B] mb-2 text-sm px-10 text-center">
            You’re not registered yet. Please complete the form to sign up.
          </p>

          <form className="space-y-6" onSubmit={handleRegister}>
            {/* Name */}
            <div>
              <label className="block text-sm mb-1 font-normal font-inter">
                Name<span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-black">
                  <img src={UserIcon} className="h-5 w-5" alt="User Icon" />
                </span>
                <input
                  type="text"
                  className="w-full pl-10 pr-4 py-2 border-2 border-[#9061DB] bg-transparent rounded-md font-inter focus:outline-none focus:ring-2 focus:ring-[#9061DB]"
                  placeholder=" Enter your name"
                  value={name}
                  maxLength={50}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (!/\d/.test(value)) {
                      setName(value);
                      setErrors((prev) => ({ ...prev, name: "" }));
                    }
                  }}
                />
              </div>
              {errors.name && (
                <p className="text-sm text-red-500 mt-1">{errors.name}</p>
              )}
            </div>

            {/* Company Name */}
            <div>
              <label className="block text-sm mb-1 font-normal font-inter">
                Company Name<span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-black">
                  <img
                    src={CompanyIcon}
                    className="h-5 w-5"
                    alt="Company Icon"
                  />
                </span>
                <input
                  type="text"
                  maxLength={50}
                  className="w-full pl-10 pr-4 py-2 border-2 border-[#9061DB] bg-transparent rounded-md font-inter focus:outline-none focus:ring-2 focus:ring-[#9061DB]"
                  placeholder="Enter your company name"
                  value={companyName}
                  onChange={(e) => {
                    setCompanyName(e.target.value);
                    setErrors((prev) => ({
                      ...prev,
                      company_name: "",
                      domain: "", // also clear domain error
                    }));
                  }}
                />
              </div>
              {errors.company_name && (
                <p className="text-sm text-red-500 mt-1">
                  {errors.company_name}
                </p>
              )}
            </div>

            {/* Domain */}
            <div>
              <div className="relative w-full flex">
                <input
                  type="text"
                  className="w-1/2 pl-4 pr-4 py-2 border-2 border-[#9061DB] bg-transparent rounded-md font-inter focus:outline-none focus:ring-2 focus:ring-[#9061DB]"
                  placeholder="Subdomain"
                  value={domain}
                  maxLength={50}
                  onChange={(e) => {
                    setDomain(e.target.value.toLowerCase());
                    setErrors((prev) => ({ ...prev, domain: "" }));
                  }}
                />
                <div className="w-1/2 flex items-center justify-center bg-[#f3f4f6] text-gray-600 rounded-r-md py-2 px-4">
                  .onzitr.com
                </div>
              </div>
              {errors.domain && (
                <p className="text-sm text-red-500 mt-1">{errors.domain}</p>
              )}
            </div>

            {/* Business Email */}
            <div>
              <label className="block text-sm mb-1 font-normal font-inter">
                Business Email
                {/* {!isEmailDisabled && <span className="text-red-500">*</span>} */}
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-black">
                  <img src={emailIcon} className="h-5 w-5" alt="Email Icon" />
                </span>
                <input
                  type="email"
                  maxLength={50}
                  className={`w-full pl-10 pr-4 py-2 border-2 rounded-md font-inter focus:outline-none focus:ring-2 ${
                    isEmailDisabled
                      ? "border-gray-400 bg-gray-100 cursor-not-allowed"
                      : "border-[#9061DB] bg-transparent"
                  }`}
                  placeholder="Enter your business email"
                  value={businessEmail}
                  onChange={(e) => {
                    if (!isEmailDisabled) {
                      setBusinessEmail(e.target.value);
                      setErrors((prev) => ({ ...prev, email: "" }));
                    }
                  }}
                  disabled={isEmailDisabled}
                  required={false} // required only if editable
                />
              </div>
              {errors.email && (
                <p className="text-sm text-red-500 mt-1">{errors.email}</p>
              )}
            </div>

            {/* Phone Number */}
            <div>
              <label className="block text-sm mb-1 font-normal font-inter">
                Mobile Number
                {!isPhoneDisabled && <span className="text-red-500">*</span>}
              </label>
              <>
                <style>
                  {`
      .react-tel-input .selected-flag {
        width: 38px !important;
        height: 94% !important;
      }

      .react-tel-input .flag-dropdown {
        top: 4px !important;
        bottom: 0px !important;
        left: 4px !important;
      }
    `}
                </style>

                <PhoneInput
                  country="in"
                  disableDropdown={isPhoneDisabled}
                  enableSearch={true}
                  enableAreaCodes={true}
                  value={phoneNumber}
                  onChange={(val, countryData) => {
                    if (!isPhoneDisabled) {
                      setPhoneNumber(val);
                      setCountryCode("+" + countryData.dialCode); // ✅ Set full dial code
                      setErrors((prev) => ({ ...prev, phone_number: "" }));
                    }
                  }}
                  inputProps={{
                    name: "phone",
                    required: !isPhoneDisabled,
                    disabled: isPhoneDisabled,
                    onKeyDown: handlePhoneInputKeyDown,
                    onFocus: (e) => {
                      // Place cursor after "+91"
                      if (e.target.value.startsWith("+91")) {
                        setTimeout(() => {
                          e.target.setSelectionRange(3, 3);
                        }, 0);
                      }
                    },
                  }}
                  inputStyle={{
                    width: "100%",
                    paddingLeft: "40px",
                    border: "2px solid #9061DB",
                    borderRadius: "0.375rem",
                    height: "40px",
                    backgroundColor: isPhoneDisabled
                      ? "#f3f4f6"
                      : "transparent",
                    color: "#000",
                    fontFamily: "inherit",
                  }}
                  buttonStyle={{
                    border: "none",
                    backgroundColor: "transparent",
                  }}
                  containerStyle={{
                    width: "100%",
                  }}
                  dropdownStyle={{
                    zIndex: 1000,
                  }}
                />
              </>

              {errors.phone_number && (
                <p className="text-sm text-red-500 mt-1">
                  {errors.phone_number}
                </p>
              )}
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm mb-1 font-normal font-inter">
                Password<span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-black">
                  <img src={LockIcon} className="h-5 w-5" alt="Lock Icon" />
                </span>
                <span
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-black cursor-pointer"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <Eye size={20} /> : <EyeOff size={20} />}
                </span>
                <input
                  type={showPassword ? "text" : "password"}
                  className="w-full pl-10 pr-4 py-2 border-2 border-[#9061DB] bg-transparent rounded-md font-inter focus:outline-none focus:ring-2 focus:ring-[#9061DB]"
                  placeholder="Enter your password"
                  maxLength={50}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setErrors((prev) => ({ ...prev, password: "" }));
                  }}
                  onBlur={(e) => evaluatePasswordStrength(e.target.value)}
                />
              </div>
              {passwordStrengthMessage && (
                <p className={`text-sm mt-1 ${passwordStrengthColor}`}>
                  {passwordStrengthMessage}
                </p>
              )}

              {errors.password && (
                <p className="text-sm text-red-500 mt-1">{errors.password}</p>
              )}
            </div>

            <div>
              <label className="block text-sm mb-1 font-normal font-inter">
                Confirm Password<span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-black">
                  <img src={LockIcon} className="h-5 w-5" alt="Lock Icon" />
                </span>
                <span
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-black cursor-pointer"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <Eye size={20} /> : <EyeOff size={20} />}
                </span>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  className="w-full pl-10 pr-4 py-2 border-2 border-[#9061DB] bg-transparent rounded-md font-inter focus:outline-none focus:ring-2 focus:ring-[#9061DB]"
                  placeholder="Enter your confirm password"
                  maxLength={50}
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setErrors((prev) => ({ ...prev, confirmPassword: "" }));
                  }}
                  onBlur={() => {
                    if (confirmPassword !== password) {
                      setErrors((prev) => ({
                        ...prev,
                        confirmPassword:
                          "Entered confirm password does not match the new password.",
                      }));
                    } else {
                      setErrors((prev) => ({ ...prev, confirmPassword: "" }));
                    }
                  }}
                />
              </div>
              {errors.confirmPassword && (
                <p className="text-sm text-red-500 mt-1">
                  {errors.confirmPassword}
                </p>
              )}
            </div>

            {/* Terms */}
            <div className="flex items-center gap-2 mt-4">
              <input
                type="checkbox"
                id="terms"
                className="w-4 h-4"
                checked={acceptTerms}
                onChange={(e) => {
                  setAcceptTerms(e.target.checked);
                  setErrors((prev) => ({ ...prev, terms: "" })); // clear error when changed
                }}
              />
              <label
                htmlFor="terms"
                className="text-sm font-inter text-gray-700"
              >
                I agree to the{" "}
                <span
                  onClick={() =>
                    window.open(
                      "http://onzitr.com/terms&condition.html",
                      "_blank"
                    )
                  }
                  className="text-[#9061DB] underline cursor-pointer"
                >
                  Terms & Conditions
                </span>{" "}
                and{" "}
                <span
                  onClick={() =>
                    window.open("https://onzitr.com/privacy.html", "_blank")
                  }
                  className="text-[#9061DB] underline cursor-pointer"
                >
                  Privacy Policy
                </span>
                .
              </label>
            </div>
            {errors.terms && (
              <span className="text-red-500 text-sm">{errors.terms}</span>
            )}

            {/* Submit */}
            <div className="flex justify-center mt-5">
              <button
                type="submit"
                className="bg-gradient-to-r from-[#4C3A68] to-[#9061DB] text-white rounded-md hover:opacity-90 transition font-inter w-full h-10"
                disabled={loading} // Disable button when loading is true
              >
                {loading ? "Signing Up..." : "Sign Up"}
              </button>
            </div>

            <p className="text-center mt-4 pb-10">
              Already have an account?{" "}
              <span
                style={{ color: "#9367D7" }}
                className="font-semibold cursor-pointer"
                onClick={handleClick}
              >
                Login
              </span>
            </p>
          </form>
        </div>
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-md max-w-sm w-full shadow-lg text-center">
            <p className="text-gray-800 mb-4">{modalMessage}</p>
            <button
              onClick={closeModal}
              className="px-4 py-2 bg-[#9061DB] text-white rounded hover:opacity-90"
            >
              OK
            </button>
          </div>
        </div>
      )}

      {showEmailConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-md max-w-sm w-full shadow-lg text-center">
            <p className="text-gray-800 mb-2 text-lg">
              You haven’t entered your email ID.
            </p>
            {countryCode !== "+91" ? (
              <p className="text-base text-red-600 mb-4">
                Email ID is mandatory for international users. Please enter your
                email to continue.
              </p>
            ) : (
              <p className="text-lg text-gray-600 mb-4">
                Do you want to continue without it?
              </p>
            )}
            <div className="flex gap-6">
              <button
                onClick={handleProceedWithoutEmail}
                className={`px-4 py-2 w-1/2 text-sm font-semibold rounded text-white ${
                  countryCode !== "+91"
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-[#9061DB] hover:opacity-90"
                }`}
                disabled={countryCode !== "+91"}
              >
                Continue
              </button>
              <button
                onClick={handleCancelWithoutEmail}
                className="px-4 py-2 w-1/2  border border-[#9061DB] text-[#9061DB] text-sm font-semibold rounded hover:bg-gray-100"
              >
                Back to Sign Up
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignUp;
